import BusinessTypeDrawer from "./BusinessTypeDrawer";
import CompanyConceptsDrawer from "./CompanyConceptsDrawer";
import SettingsPageLayout, { SettingsPageLoading, settingsPageTitleClass } from "../../../components/shared/SettingsPageLayout";
import GoogleHoursAudit from "../locations_integrations_hours_settings/GoogleHoursAudit";
import ChecklistActions, { checklistActionLabel } from "./ChecklistActions";
import { companyConfigurationTasks, PROCESS_TASK_KEYS, DATA_MODAL_KEYS, displayedJourney } from "./dataPreparationJourney";
import { OnboardingCaret, OnboardingCollapse } from "./OnboardingDisclosure";
import React from "react";
import AssistantRoundedIcon from "@mui/icons-material/AssistantRounded";
import { toast } from "react-toastify";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import CompanyNameSidebar from "../home/top_bar_components/CompanyNameSidebar";
import {
  API_get_onboarding_summary,
  API_update_onboarding_preferences,
} from "../../../constants";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import { handleOnboardingAction, onboardingCompanyStoreId } from "./handleOnboardingAction";
import { useOnboardingIntent } from "./onboardingNavigation";
import { completedTaskDetails } from "./completedTaskDetails";
import OnboardingFeatureEditor from "./OnboardingFeatureEditor";
import OnboardingCompanyPipelineDrawer from "./OnboardingCompanyPipelineDrawer";
import OnboardingRecommendedSetupCard from "./OnboardingRecommendedSetupCard";
import LaborStandardsOptimizationDrawer from "../labor_model/LaborStandardsOptimizationDrawer";


// "not_applicable": the step is not part of this company's starting modules.
// It counts as done for progress and never becomes the next step.
function isComplete(status) {
  return status === "complete" || status === "completed" || status === "not_applicable";
}

function isInternalTask(task) {
  return (task?.audience || "customer") === "internal";
}

const STATUS_STYLES = {
  in_review: "border-sky-200 bg-sky-50 text-sky-700",
  in_progress: "border-sky-200 bg-sky-50 text-sky-700",
  blocked: "border-amber-200 bg-amber-50 text-amber-800",
  not_started: "border-slate-200 bg-slate-100 text-slate-700",
  not_applicable: "border-slate-200 bg-slate-50 text-slate-400",
};

// Drawers that submit to the owner-or-Rightwork endpoints (backfills, forecasts,
// earned labor, the daily pipeline). Mirrors the automation gate in
// company_info/views/onboarding.py; opening them without it only ends in a 403.
const OWNER_ONLY_MODALS = new Set([
  "company_initial_backfill",
  "company_initial_forecast",
  "company_earned_labor_backfill",
  "store_backfills",
  "company_pipeline",
]);

// Fallback only: the summary carries the configured link (setupCallUrl).
const CALENDLY_URL = "https://calendly.com/will-getrightwork/30min";
const SUPPORT_CHANNEL_STORAGE_KEY = "onboarding-support-channel-id";
const CONFETTI_COLORS = ["#6d28d9", "#14b8a6", "#f59e0b", "#ef4444", "#0ea5e9", "#22c55e"];

function StatusPill({ status }) {
  if (status === "not_applicable") {
    return (
      <span
        className={`inline-flex shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_STYLES.not_applicable}`}
      >
        not applicable
      </span>
    );
  }
  if (isComplete(status) || status === "in_review" || status === "not_started" || !status) return null;
  return (
    <span
      className={`inline-flex shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.not_started}`}
    >
      {(status || "not_started").replace("_", " ")}
    </span>
  );
}

function getLoadingActionKey(action) {
  if (!action?.taskKey) {
    return null;
  }
  return [action.taskKey, action.store_id || "company", action.action || "complete"].join(":");
}

function isSavingTaskAction(action) {
  return Boolean(action?.taskKey);
}


function ChecklistConfetti() {
  return (
    <div className="onboarding-checklist-confetti" aria-hidden="true">
      {Array.from({ length: 16 }).map((_, index) => (
        <span
          key={index}
          className="onboarding-checklist-confetti-piece"
          style={{
            left: `${6 + (index * 6)}%`,
            animationDelay: `${index * 40}ms`,
            backgroundColor: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
            "--drift": `${(index % 2 === 0 ? -1 : 1) * (0.35 + ((index % 5) * 0.12))}`,
            transform: `rotate(${(index % 6) * 18}deg)`,
          }}
        />
      ))}
    </div>
  );
}

function ChecklistItem({ task, onAction, isActionLoading, actionsDisabled = false, setupCallUrl = CALENDLY_URL, children, summary }) {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  React.useEffect(() => setDetailsOpen(false), [task.key, task.status]);
  const completed = completedTaskDetails(task, summary);
  const notApplicable = task.status === "not_applicable";
  const taskComplete = isComplete(task.status);
  const isFeatureEditor = ["starting_modules_selected", "hours_of_operation_reviewed"].includes(task.key) && Boolean(children);
  const hasInlineDetails = isFeatureEditor || (taskComplete && Boolean(completed?.inline));
  const skipped = taskComplete && task.undoAction?.label?.toLowerCase() === "undo skip";
  // Keep normal rows to one line; explain only unavailable or blocked steps.
  const description = notApplicable || task.moduleOff ? task.description : "";
  const mainAction = taskComplete ? completed?.action : task.primaryAction;
  const menuActions = taskComplete ? [task.undoAction].filter(Boolean)
    : [task.skipAction, task.secondaryAction].filter(Boolean);
  const actionBusy = [task.undoAction, task.primaryAction, task.secondaryAction, task.skipAction]
    .filter(Boolean).some((action) => isActionLoading(action));

  return (
    <div className="border-b border-slate-100 py-3 last:border-b-0">
      <div className="flex min-h-[40px] items-center gap-3 md:flex-wrap">
        <div className="shrink-0">
          {notApplicable ? (
            <RadioButtonUncheckedRoundedIcon className="text-slate-200" fontSize="small" />
          ) : taskComplete ? (
            <CheckCircleRoundedIcon className="text-emerald-500" fontSize="small" />
          ) : (
            <RadioButtonUncheckedRoundedIcon className="text-slate-300" fontSize="small" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <p className={`text-sm font-medium ${notApplicable ? "text-slate-400" : taskComplete ? "text-slate-700" : "text-slate-900"}`}>
              {task.title}
            </p>
            {skipped ? <span className="inline-flex shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-500">Skipped</span>
              : !task.blockingReason && <StatusPill status={task.status} />}
          </div>
          {description && (
            <p className="mt-0.5 text-xs text-slate-500">{description}</p>
          )}
          {task.blockingReason && (
            <p className="mt-0.5 text-xs text-amber-700">{task.blockingReason}</p>
          )}
        </div>
        {!notApplicable && !task.moduleOff && (mainAction || menuActions.length > 0) && (
          <ChecklistActions title={task.title}
            main={mainAction ? { ...mainAction, label: detailsOpen ? "Close" : checklistActionLabel(task, mainAction, taskComplete) } : null}
            options={menuActions} disabled={actionsDisabled} busy={actionBusy}
            expanded={hasInlineDetails ? detailsOpen : undefined}
            onMain={() => hasInlineDetails ? setDetailsOpen((value) => !value) : onAction(mainAction)}
            onOption={onAction} />
        )}
      </div>
      {isFeatureEditor ? <OnboardingCollapse open={detailsOpen}>{children}</OnboardingCollapse> : !taskComplete && children}
      {detailsOpen && completed?.inline === "results" && (
        <div className="ml-8 mt-3 rounded-xl bg-slate-50 p-4 text-xs text-slate-600 sm:ml-0">
          <p className="font-medium">Previous setup attempts</p>
          <p className="mt-1">These records show past attempts, not the current health of your data. This checklist step has been marked complete.</p>
          {completed.results.length ? completed.results.map((job, index) => (
            <div key={index} className="mt-2">
              <p>{job.name}: {job.status || "Unknown"}</p>
              {job.date && <p>{job.date}</p>}
            </div>
          )) : <p className="mt-2">No recorded job results are available. This step may have been completed from existing data or marked reviewed.</p>}
          <p className="mt-2">Undo review only reopens the checklist step; it does not reverse a completed run.</p>
        </div>
      )}

    </div>
  );
}

function CollapsibleChecklist({
  title,
  subtitle,
  tasks = [],
  onAction,
  defaultOpen,
  isActionLoading,
  actionsDisabled = false,
  setupCallUrl = CALENDLY_URL,
  renderTaskDetails,
  children,
  revealToken,
  summary,
}) {
  // Steps that do not apply to this company's modules are listed for
  // context but never counted, so a fresh training-first company does not
  // read as mostly done before any work happened.
  // Steps of a module that is off (not applicable, or completed before the
  // module was turned off) are left out of the totals on both sides.
  const counted = tasks.filter((t) => t.status !== "not_applicable" && !t.moduleOff);
  const completeCount = counted.filter((t) => isComplete(t.status)).length;
  const blockedCount = tasks.filter((t) => t.status === "blocked").length;
  const hasIncomplete = completeCount < counted.length;
  const [open, setOpen] = React.useState(defaultOpen ?? hasIncomplete);
  React.useEffect(() => { if (revealToken) setOpen(true); }, [revealToken]);
  const [showConfetti, setShowConfetti] = React.useState(false);
  const previousHasIncompleteRef = React.useRef(hasIncomplete);
  const progressPercent = Math.round((completeCount / (counted.length || 1)) * 100);

  React.useEffect(() => {
    const wasIncomplete = previousHasIncompleteRef.current;
    previousHasIncompleteRef.current = hasIncomplete;

    if (!wasIncomplete || hasIncomplete) {
      return undefined;
    }

    setShowConfetti(true);
    const collapseTimer = setTimeout(() => setOpen(false), 900);
    const resetTimer = setTimeout(() => setShowConfetti(false), 1600);

    return () => {
      clearTimeout(collapseTimer);
      clearTimeout(resetTimer);
    };
  }, [hasIncomplete]);

  return (
    <div className="relative rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {showConfetti && <ChecklistConfetti />}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-5 py-4 text-left rounded-2xl transition-colors duration-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-base font-medium text-slate-900">{title}</h3>
            <span className="text-xs text-slate-500">
              {completeCount} of {counted.length} complete
            </span>
            {subtitle && (
              <span className="text-xs text-slate-400">{subtitle}</span>
            )}
          </div>
          {progressPercent > 0 && (
            <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
              <div
                className="h-1.5 rounded-full bg-violet-700 transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}
          {!open && hasIncomplete && blockedCount > 0 && (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              {blockedCount > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                  <WarningAmberRoundedIcon style={{ fontSize: 14 }} />
                  {blockedCount} blocked
                </span>
              )}
            </div>
          )}
        </div>
        <OnboardingCaret open={open} />
      </button>
      {(tasks.length > 0 || children) && (
        <OnboardingCollapse open={open}>
          <div className="border-t border-slate-100 px-5 pb-4">
            {counted.map((task) => (
              <ChecklistItem
                key={task.key}
                task={task}
                summary={summary}
                onAction={onAction}
                isActionLoading={isActionLoading}
                actionsDisabled={actionsDisabled}
                setupCallUrl={setupCallUrl}
              >
                {renderTaskDetails?.(task)}
              </ChecklistItem>
            ))}
            {children}
          </div>
        </OnboardingCollapse>
      )}
    </div>
  );
}

export default function OnboardingCenterDisplay(props) {
  const [preparationPhase, setPreparationPhase] = React.useState(null);
  const summaryRequestSequenceRef = React.useRef(0);
  const companyOptions = React.useMemo(() => {
    const unique = [];
    props.storeNames?.forEach((store) => {
      if (!unique.some((company) => company.id === store.company)) {
        unique.push({
          id: store.company,
          name: store.company_info?.company_name || store.company_info?.name,
        });
      }
    });
    return unique;
  }, [props.storeNames]);

  const [selectedCompanyId, setSelectedCompanyId] = React.useState(props.settingsCompanyId || props.homeCompany);
  const [summary, setSummary] = React.useState(null);
  const [summaryError, setSummaryError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [requestedSection, setRequestedSection] = React.useState(null);
  const [dataRevealToken, setDataRevealToken] = React.useState(0);
  const [loadingActionKeys, setLoadingActionKeys] = React.useState({});
  const [activeModal, setActiveModal] = React.useState(null);
  const [recommendedSetupBlocked, setRecommendedSetupBlocked] = React.useState(true);
  const [recommendedSetupReady, setRecommendedSetupReady] = React.useState(false);
  const [savingPrimaryGoal, setSavingPrimaryGoal] = React.useState(false);
  // From the selected company's summary: the home-store role does not
  // describe a multi-company user's standing at the company shown here.
  const isRightworkInternal = summary?.isRightworkInternal === true;
  // Backfills, the recommended setup, and the pipeline toggle are started by
  // the account owner or Rightwork; other admins see the checklist without
  // those controls instead of a card that can only fail.
  const canRunAutomation = summary?.canRunAutomation !== false;
  // The recommended run is scheduling-module work; the server refuses it
  // while Scheduling is off, so the card is not offered then either.
  const schedulingModuleOn = summary?.company?.modules?.scheduling !== false;
  const recommendedSetupAvailable =
    Boolean(summary?.recommendedSetupEnabled) && canRunAutomation && schedulingModuleOn;
  const setupCallUrl = summary?.setupCallUrl || CALENDLY_URL;
  const recommendedSetupGateReady = Boolean(summary) && (
    !recommendedSetupAvailable || recommendedSetupReady
  );
  // An admin who cannot start the run does not mount its card, so the run's
  // own gate never reports; the summary flag keeps manual actions paused for
  // them too (the server refuses those changes while a run is active).
  const setupRunActive = summary?.setupRunActive === true;
  const manualActionsBlocked = !recommendedSetupGateReady || (
    recommendedSetupAvailable && recommendedSetupBlocked
  ) || setupRunActive;

  const selectCompany = React.useCallback((companyId) => {
    summaryRequestSequenceRef.current += 1;
    setSummary(null);
    setSummaryError(null);
    setLoading(true);
    setActiveModal(null);
    setRecommendedSetupBlocked(true);
    setRecommendedSetupReady(false);
    setSelectedCompanyId(companyId);
  }, []);

  const handleRecommendedSetupState = React.useCallback((blocked, state = {}) => {
    setRecommendedSetupBlocked(Boolean(blocked));
    setRecommendedSetupReady(Boolean(state.ready));
  }, []);

  const refreshSummary = React.useCallback(async (background = false) => {
    background = background === true;
    if (!selectedCompanyId) {
      return;
    }
    const requestSequence = ++summaryRequestSequenceRef.current;
    if (!background) setSummaryError(null);
    if (!background) setLoading(true);
    try {
      const summaryResponse = await getRightworkAPI(API_get_onboarding_summary, {
        company_id: selectedCompanyId,
      });
      if (requestSequence === summaryRequestSequenceRef.current) {
        setSummaryError(null);
        setSummary(summaryResponse?.data?.redux?.payload || null);
      }
    } catch (error) {
      if (requestSequence === summaryRequestSequenceRef.current) {
        if (!background || [403, 404].includes(error?.response?.status)) setSummary(null);
        const denied = [403, 404].includes(error?.response?.status);
        setSummaryError(denied
          ? "Onboarding is not available for this company. Choose another company or contact support."
          : "Unable to load onboarding. Please try again.");
      }
    } finally {
      if (requestSequence === summaryRequestSequenceRef.current) {
        setLoading(false);
      }
    }
  }, [selectedCompanyId]);

  React.useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  React.useEffect(() => {
    let cancelled = false;
    let timer;
    const poll = async () => {
      if (document.visibilityState !== "hidden") await refreshSummary(true);
      if (!cancelled) timer = setTimeout(poll, 10000);
    };
    timer = setTimeout(poll, 10000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [refreshSummary]);

  // A setup action confirmed through the Setup Assistant (Lumi) or Rovis
  // changes the checklist; refetch on any refresh signal after mount.
  const opsRefreshTimestamp = props.opsRefreshSignal?.timestamp;
  const lastOpsRefreshRef = React.useRef(opsRefreshTimestamp);
  React.useEffect(() => {
    if (!opsRefreshTimestamp || opsRefreshTimestamp === lastOpsRefreshRef.current) {
      return;
    }
    lastOpsRefreshRef.current = opsRefreshTimestamp;
    refreshSummary();
  }, [opsRefreshTimestamp, refreshSummary]);

  // A recommended-setup action confirmed through the assistant should also
  // refetch the latest run so the card catches up without waiting on polling.
  const opsRefreshTypes = props.opsRefreshSignal?.types;
  const recommendedSetupRefreshToken = React.useMemo(() => {
    if (!opsRefreshTimestamp || !Array.isArray(opsRefreshTypes)) return null;
    const relevant = opsRefreshTypes.includes("setup_run") || opsRefreshTypes.includes("setup");
    return relevant ? opsRefreshTimestamp : null;
  }, [opsRefreshTimestamp, opsRefreshTypes]);

  React.useEffect(() => {
    if (!requestedSection) {
      return;
    }
    const section = document.getElementById(`onboarding-section-${requestedSection}`);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [requestedSection, summary]);

  useOnboardingIntent("onboardingCenter", (pendingIntent) => {
    if (pendingIntent.section) setRequestedSection(pendingIntent.section);
    if (pendingIntent.section === "data" || DATA_MODAL_KEYS.has(pendingIntent.modal)) {
      setPreparationPhase(pendingIntent.preparationPhase || null);
      setDataRevealToken((value) => value + 1);
      setRequestedSection("data");
      document.getElementById("onboarding-section-data")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (pendingIntent.modal && !manualActionsBlocked) {
      setActiveModal({ key: pendingIntent.modal, props: pendingIntent.modalProps || {} });
    }
  }, Boolean(summary) && recommendedSetupGateReady);

  React.useEffect(() => {
    if (manualActionsBlocked && activeModal) {
      setActiveModal(null);
    }
  }, [activeModal, manualActionsBlocked]);

  const openSupportChat = React.useCallback(() => {
    if (!selectedCompanyId || typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(
      SUPPORT_CHANNEL_STORAGE_KEY,
      `company-${selectedCompanyId}-support`
    );
    props.setCurrentTab("chat");
  }, [props, selectedCompanyId]);

  const openModal = React.useCallback((modalKey, modalProps = null) => {
    if (DATA_MODAL_KEYS.has(modalKey)) {
      setDataRevealToken((value) => value + 1);
      setRequestedSection("data");
      document.getElementById("onboarding-section-data")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setActiveModal(modalKey ? { key: modalKey, props: modalProps || {} } : null);
  }, []);

  const closeModal = React.useCallback(() => {
    setActiveModal(null);
  }, []);

  const actionHandlerOptions = React.useMemo(() => ({
    companyId: selectedCompanyId,
    companyStoreId: summary?.storeSummaries?.[0]?.store_id || onboardingCompanyStoreId(selectedCompanyId, props.storeNames),
    setCurrentTab: props.setCurrentTab,
    homeStore: props.homeStore,
    changeHomeStore: props.change_home_store,
    refreshSummary,
    setRequestedSection,
    openModal,
  }), [
    selectedCompanyId,
    summary?.storeSummaries,
    props.storeNames,
    props.setCurrentTab,
    props.homeStore,
    props.change_home_store,
    refreshSummary,
    setRequestedSection,
    openModal,
  ]);

  const onAction = React.useCallback(
    async (action) => {
      if (manualActionsBlocked) {
        return;
      }
      if (!canRunAutomation && (action?.automation || OWNER_ONLY_MODALS.has(action?.modal))) {
        toast.error("Only the account owner or Rightwork can start data setup for this company.");
        return;
      }
      const loadingActionKey = isSavingTaskAction(action) ? getLoadingActionKey(action) : null;
      if (loadingActionKey) {
        setLoadingActionKeys((current) => ({ ...current, [loadingActionKey]: true }));
      }

      try {
        const updated = await handleOnboardingAction(action, actionHandlerOptions);
        if (updated && ["manual_sales", "pos_sales"].includes(action?.action)) props.onFeaturesChanged?.();
      } finally {
        // Progress may have moved either way (complete or undo); let the
        // setup assistant re-read the summary.
        props.onProgressChange?.();
        if (loadingActionKey) {
          setLoadingActionKeys((current) => {
            const next = { ...current };
            delete next[loadingActionKey];
            return next;
          });
        }
      }
    },
    [actionHandlerOptions, canRunAutomation, manualActionsBlocked, props.onProgressChange, props.onFeaturesChanged]
  );

  const isActionLoading = React.useCallback(
    (action) => Boolean(loadingActionKeys[getLoadingActionKey(action)]),
    [loadingActionKeys]
  );

  // Starting modules: the backend records the choice, applies the feature
  // changes to company defaults and optionally existing locations, and completes the
  // step. The member store list is refetched so the nav follows.
  const { onFeaturesChanged, onProgressChange } = props;
  const applyFeatureChanges = React.useCallback(
    async (flags, applyToExisting) => {
      if (!selectedCompanyId || manualActionsBlocked) {
        return;
      }
      setSavingPrimaryGoal(true);
      try {
        await postRightworkAPI(API_update_onboarding_preferences, {
          company_id: selectedCompanyId,
          feature_flags: flags,
          apply_to_existing_locations: Boolean(applyToExisting),
        });
        await refreshSummary();
        onFeaturesChanged?.();
        onProgressChange?.();
      } catch (error) {
        console.error("Unable to update starting modules", error);
        // The card snaps back to the recorded value on the next render.
        toast.error("Could not update features.");
        throw error;
      } finally {
        setSavingPrimaryGoal(false);
      }
    },
    [manualActionsBlocked, onFeaturesChanged, onProgressChange, refreshSummary, selectedCompanyId]
  );

  // Rightwork-internal steps are shown to Rightwork only; the customer's
  // checklist never lists them.
  const companyTasks = React.useMemo(() => {
    const tasks = summary?.company?.tasks || [];
    return {
      customer: companyConfigurationTasks(tasks.filter((task) => !isInternalTask(task) && task.status !== "not_applicable" && !task.moduleOff)),
      internal: tasks.filter(task => isInternalTask(task) && task.status !== "not_applicable" && !task.moduleOff),
    };
  }, [summary]);

  if (loading && !summary) {
    return (
      <SettingsPageLoading />
    );
  }

  return (
    <SettingsPageLayout>
      <div>
      <div className="flex flex-row items-center justify-between gap-4 sm:flex-col sm:items-stretch sm:justify-start">
        <div>
          <h1 className={settingsPageTitleClass}>Onboarding center</h1>
        </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {props.onOpenSetupAssistant && summary && (
              <button type="button" onClick={() => props.onOpenSetupAssistant(selectedCompanyId)}
                title="Let Lumi guide your setup"
                className="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-violet-700 bg-violet-700 px-3 text-sm font-medium text-white hover:bg-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600">
                <AssistantRoundedIcon style={{ fontSize: 16 }} />
                Setup assistant
              </button>
            )}
            <a
              href={setupCallUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
            >
              <CalendarMonthRoundedIcon style={{ fontSize: 16 }} />
              Book a call
            </a>
            <button
              onClick={openSupportChat}
              className="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
            >
              <ForumRoundedIcon style={{ fontSize: 16 }} className="text-violet-700" />
              Support chat
            </button>
          </div>
      </div>
      {!props.settingsCompanyId && (
        <div className="mt-4 w-full max-w-xs">
          <CompanyNameSidebar
            companyOptions={companyOptions}
            selectedCompanyId={selectedCompanyId}
            setSelectedCompanyId={selectCompany}
          />
        </div>
      )}

      {summaryError && (
        <div role="alert" className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-600">{summaryError}</p>
          <button type="button" onClick={refreshSummary} className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
            Try again
          </button>
        </div>
      )}
      {summary && <>
      {/* Manual setup tools */}
      <section id="onboarding-section-company" className="mt-6">
        {manualActionsBlocked && <p className="mb-3 text-xs text-slate-500">Setup actions are paused while automatic setup is being checked or running.</p>}
        {!canRunAutomation && <p className="mb-3 text-xs text-slate-500">Data setup runs are managed by the account owner or Rightwork.</p>}
        <CollapsibleChecklist
          summary={summary}
          title={summary?.company?.company_name || "Company"}
          tasks={companyTasks.customer}
          revealToken={dataRevealToken}
          renderTaskDetails={(task) => task.key === "starting_modules_selected" ? (
            <OnboardingFeatureEditor
              companyId={selectedCompanyId}
              pipelineEnabled={summary?.company?.pipeline_enabled}
              onApply={applyFeatureChanges}
              saving={savingPrimaryGoal}
              disabled={manualActionsBlocked}
            />
          ) : task.key === "hours_of_operation_reviewed" ? (
            <div className="ml-8 mt-3 sm:ml-0">
              <GoogleHoursAudit key={`onboarding-hours:${selectedCompanyId}`}
                locations={(summary?.storeSummaries || []).map(store => ({ storeId: store.store_id, locationName: store.store_name }))}
                onSaved={async () => { await refreshSummary(); props.onProgressChange?.(); }} />
              <button type="button" disabled={manualActionsBlocked}
                className="text-sm font-medium text-violet-700 hover:text-violet-800 disabled:opacity-50"
                onClick={() => onAction(isComplete(task.status) ? completedTaskDetails(task, summary)?.action : task.primaryAction)}>
                Review or edit saved hours
              </button>
            </div>
          ) : null}
          onAction={onAction}
          defaultOpen
          isActionLoading={isActionLoading}
          actionsDisabled={manualActionsBlocked}
          setupCallUrl={setupCallUrl}
        />
      {schedulingModuleOn && <section id="onboarding-section-data" className="mt-6">
        <OnboardingRecommendedSetupCard
          manualModelTask={!summary.recommendedSetupEnabled ? (summary.company.tasks || []).find((task) => task.key === "company_labor_standard_optimization") : null}
          onOpenLaborModel={
            !summary.recommendedSetupEnabled && canRunAutomation && !manualActionsBlocked &&
            displayedJourney(null, summary.storeSummaries || [])[0].status === "completed" &&
            !(summary.company.tasks || []).some((task) => task.key === "roles_reviewed" && !isComplete(task.status))
              ? () => (summary.company.tasks || []).some((task) => task.key === "company_labor_standard_optimization" && (task.status === "complete" || task.jobStatus?.labor_standard_optimization?.applied_to_sandbox))
                ? onAction({ tab: "laborModel" }) : openModal("company_labor_standard_optimization") : null
          }
          revealToken={dataRevealToken}
          requestedPhase={preparationPhase}
          internalOnly
          additionalTasks={isRightworkInternal ? companyTasks.internal : []}
          renderAdditionalTask={(task) => (
            <ChecklistItem key={task.key} task={task} summary={summary} onAction={onAction}
              isActionLoading={isActionLoading} actionsDisabled={manualActionsBlocked}
              setupCallUrl={setupCallUrl} />
          )}
          companyId={selectedCompanyId}
          storeSummaries={summary?.storeSummaries || []}
          refreshSummary={refreshSummary}
          refreshToken={recommendedSetupRefreshToken}
          onActiveChange={handleRecommendedSetupState}
          startBlockedReason={
            !canRunAutomation ? "Only the account owner or Rightwork can start data preparation." :
            !summary?.recommendedSetupEnabled ? "Automatic data processing is not enabled in this environment." :
            (summary.company.tasks || []).some((task) => task.key === "roles_reviewed" && !isComplete(task.status))
              ? "Review departments and roles before starting."
              : (summary.storeSummaries || []).some((store) => !["toast", "square"].includes(store.pos_provider))
                ? "Automatic setup requires a supported POS connection at every location."
                : (summary.storeSummaries || []).some((store) => (store.dataPreparationProgress || []).some((run) => ["pending", "running"].includes(run.status)))
                  ? "Your current data run must finish before starting automatic setup." : ""
          }
        />
      </section>}
      </section>

      {/* Location checklists */}
      <section className="mt-8">
        <div className="mb-3 flex items-center gap-2">
          <LocationOnOutlinedIcon fontSize="small" className="text-slate-400" />
          <h2 className="text-sm font-medium text-slate-600">Locations</h2>
        </div>
        <div className="space-y-3">
          {summary?.storeSummaries?.map((storeSummary) => (
            <CollapsibleChecklist
              summary={summary}
              key={storeSummary.store_id}
              title={storeSummary.store_name}
              subtitle={storeSummary.is_billable ? "· billable" : "· not billable yet"}
              tasks={(storeSummary.tasks || []).filter((task) => !isInternalTask(task) && !PROCESS_TASK_KEYS.has(task.key))}
              onAction={onAction}
              defaultOpen={false}
              isActionLoading={isActionLoading}
              actionsDisabled={manualActionsBlocked}
              setupCallUrl={setupCallUrl}
            />
          ))}
        </div>
      </section>

      </>}
      </div>
      <BusinessTypeDrawer open={activeModal?.key === "business_type"} onClose={closeModal} summary={summary} onSaved={async () => { await refreshSummary(); props.onProgressChange?.(); }} />
      <CompanyConceptsDrawer open={activeModal?.key === "company_concepts"} onClose={closeModal} summary={summary} onSaved={async () => { await refreshSummary(); props.onProgressChange?.(); }} />
      <OnboardingCompanyPipelineDrawer
        open={activeModal?.key === "company_pipeline"}
        onClose={closeModal}
        summary={summary}
        onSubmitted={refreshSummary}
      />
      <LaborStandardsOptimizationDrawer
        open={activeModal?.key === "company_labor_standard_optimization"}
        onClose={closeModal}
        companyId={summary?.company?.id}
        companyName={summary?.company?.company_name}
        stores={(summary?.storeSummaries || []).filter((storeSummary) => !activeModal?.props?.storeIds || activeModal.props.storeIds.includes(storeSummary.store_id)).map((storeSummary) => ({
          id: storeSummary.store_id,
          store_name: storeSummary.store_name,
          status: "open",
          onboarding_status: storeSummary.onboarding_status,
          pos_provider: storeSummary.pos_provider,
          manual_sales_input_enabled: storeSummary.manual_sales_input_enabled,
        }))}
        onSubmitted={refreshSummary}
        onCompleted={refreshSummary}
        onApplied={async () => { await refreshSummary(); closeModal(); onAction({ tab: "laborModel" }); }}
      />
    </SettingsPageLayout>
  );
}
